import { ChevronRight, Lock, Package } from "lucide-react";
import { useMemo } from "react";
import { Link } from "react-router-dom";

import { EmptyState } from "@/components/EmptyState";
import { PageContainer } from "@/components/PageContainer";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useMe } from "@/hooks/useMe";
import { useModuli } from "@/hooks/useModuli";
import { useMyPermessi } from "@/hooks/usePermessi";
import { isMaitimeStaff } from "@/lib/nav-config";
import { hasPermesso } from "@/lib/permessi";
import { cn } from "@/lib/utils";
import type { ModuloStato } from "@/types/api";

const STATO_LABEL: Record<ModuloStato, string> = {
  roadmap: "in roadmap",
  anteprima: "anteprima",
  beta: "beta",
  live: "live",
  dismesso: "dismesso",
};

const STATO_DOT: Record<ModuloStato, string> = {
  roadmap: "bg-muted-foreground/40",
  anteprima: "bg-warning",
  beta: "bg-primary",
  live: "bg-success",
  dismesso: "bg-muted-foreground/20",
};

export function ModuliPage() {
  const { data: moduli = [], isLoading } = useModuli();
  const { data: me } = useMe();
  const { data: perms } = useMyPermessi();
  const staff = isMaitimeStaff(me ?? null);

  const visibleModuli = useMemo(() => {
    if (staff) return moduli;
    // Cliente: solo i moduli funzionali (M1-M5). Niente INFRA / LIAISON (interni Maitime).
    return moduli.filter((m) => m.tipo === "modulo_funzionale");
  }, [moduli, staff]);

  return (
    <PageContainer>
      <header className="space-y-1">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">Sezione</p>
        <h1 className="text-3xl font-semibold tracking-tight text-foreground">Moduli</h1>
        <p className="text-sm text-muted-foreground max-w-2xl pt-1">
          I moduli del progetto Mister Pet. Ogni modulo ha un ciclo di vita (roadmap →
          anteprima → beta → live) e un referente cliente. Clicca su un modulo per il dettaglio.
        </p>
      </header>

      {isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : visibleModuli.length === 0 ? (
        <EmptyState
          icon={Package}
          title="Nessun modulo disponibile"
          description="Non ci sono moduli da mostrare al momento."
        />
      ) : (
        <ul className="rounded-lg border bg-card divide-y">
          {visibleModuli.map((m) => {
            const abilitato = staff || hasPermesso(perms, "modulo", m.codice);
            const content = (
              <>
                <span className="font-mono text-xs text-muted-foreground w-14 shrink-0">
                  {m.codice}
                </span>
                <div className="min-w-0 flex-1">
                  <p
                    className={cn(
                      "font-medium text-sm leading-snug",
                      !abilitato && "text-muted-foreground"
                    )}
                  >
                    {m.nome}
                  </p>
                  {m.sottotitolo && (
                    <p className="text-xs text-muted-foreground truncate mt-0.5">
                      {m.sottotitolo}
                    </p>
                  )}
                </div>
                {m.owner_cliente_label && (
                  <span className="text-xs text-muted-foreground hidden md:inline truncate max-w-[200px] shrink-0">
                    {m.owner_cliente_label}
                  </span>
                )}
                {abilitato ? (
                  <Badge variant="outline" className="shrink-0">
                    <span className={cn("size-1.5 rounded-full", STATO_DOT[m.stato])} />
                    {STATO_LABEL[m.stato]}
                  </Badge>
                ) : (
                  <Badge
                    variant="outline"
                    className="shrink-0 text-muted-foreground bg-muted/50"
                    title="Per accedere richiedi l'abilitazione a Maitime"
                  >
                    <Lock className="size-3" />
                    accesso non abilitato
                  </Badge>
                )}
                {abilitato ? (
                  <ChevronRight className="size-4 text-muted-foreground/50 group-hover:text-muted-foreground transition-colors shrink-0" />
                ) : (
                  <span className="size-4 shrink-0" />
                )}
              </>
            );
            return (
              <li key={m.id}>
                {abilitato ? (
                  <Link
                    to={`/moduli/${m.slug}`}
                    className="flex items-center gap-4 px-4 py-3.5 hover:bg-muted/40 transition-colors group"
                  >
                    {content}
                  </Link>
                ) : (
                  <div
                    className="flex items-center gap-4 px-4 py-3.5 opacity-70 cursor-not-allowed"
                    aria-disabled
                  >
                    {content}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </PageContainer>
  );
}
